<script lang="ts">
	import { onMount } from 'svelte';
	import type { User } from 'firebase/auth';
	import LoginOrSignUpModal from '$components/LoginOrSignUpModal.svelte';
	import LandingPageLayout from '$components/LandingPageLayout.svelte';
	import CTAHeadless from '$components/CTAHeadless.svelte';
	import IliadLogo from '$components/IliadLogo.svelte';
	import ToolCard from '$components/ToolCard.svelte';
	import TextureShowcase from '$components/TextureShowcase.svelte';
	import AqueductDemo from '$components/AqueductDemo.svelte';
	import { AQUEDUCT_PROJECT_JS_PATH } from '$lib/generated/aqueductRuntimeAssets';
	import { getAllTextures } from '$lib/textureShowcaseData';
	import { logLandingBoot } from '$lib/landingBootLog';
	import { AQUEDUCT_SITE_ORIGIN } from '$lib/siteConfig';
	import { analytics } from '$lib/analytics';

	logLandingBoot('root route: component script evaluated');

	let user: User | null | undefined = undefined;
	let loginOrSignUpModal: LoginOrSignUpModal;
	let loginOrSignupWhichModal: 'login' | 'sign-up' = 'sign-up';
	let loadTextureShowcaseImages = false;
	let textureShowcaseFallbackTimeoutId: ReturnType<typeof setTimeout> | undefined;

	const texturesLink = '/canvas';
	const materialsLink = '/material-generator';

	// Fallback only; CTA clicks set this to the tool's own href. Default to the
	// root page, never a hardcoded tool route.
	let redirectAfterLogin: string | undefined = '/';

	// Aqueduct lives on its own site; this is a cross-site link.
	const aqueductLink = AQUEDUCT_SITE_ORIGIN;

	const showcaseTexturesStartedAt = performance.now();
	const showcaseTextures = getAllTextures();
	logLandingBoot('root route: texture showcase data loaded', {
		duration: Number((performance.now() - showcaseTexturesStartedAt).toFixed(1)),
		count: showcaseTextures.length
	});

	function enableTextureShowcaseImages(reason: string): void {
		if (loadTextureShowcaseImages) return;
		loadTextureShowcaseImages = true;
		if (textureShowcaseFallbackTimeoutId !== undefined) {
			clearTimeout(textureShowcaseFallbackTimeoutId);
			textureShowcaseFallbackTimeoutId = undefined;
		}
		logLandingBoot('root route: texture showcase images enabled', { reason });
	}

	onMount(() => {
		logLandingBoot('root route: mounted', {
			documentReadyState: document.readyState
		});
		textureShowcaseFallbackTimeoutId = setTimeout(() => {
			enableTextureShowcaseImages('fallback-timeout');
		}, 10_000);

		return () => {
			if (textureShowcaseFallbackTimeoutId !== undefined) {
				clearTimeout(textureShowcaseFallbackTimeoutId);
				textureShowcaseFallbackTimeoutId = undefined;
			}
		};
	});
</script>

<svelte:head>
	<title>Iliad: Fully editable procedural material graphs</title>
	<meta
		name="description"
		content="Node-based procedural material engine with a built-in AI assistant. Diffusion-based texture and PBR tools."
	/>
	<meta property="og:title" content="Iliad: Fully editable procedural material graphs" />
	<meta
		property="og:description"
		content="Node-based procedural material engine with a built-in AI assistant. Diffusion-based texture and PBR tools."
	/>
	<meta property="og:url" content="https://iliad.ai" />
	<meta property="og:type" content="website" />
	<meta property="og:image" content="https://storage.iliad.ai/og-image.png" />
	<meta name="twitter:card" content="summary_large_image" />
	<script defer type="application/ld+json">
		{
			"@context": "https://schema.org",
			"@type": "WebApplication",
			"name": "Iliad",
			"description": "Generate custom seamless textures and PBR maps.",
			"url": "https://iliad.ai",
			"applicationCategory": "Design",
			"offers": [
				{
					"@type": "Offer",
					"price": "0",
					"priceCurrency": "USD",
					"description": "Free tier: Daily limited access.",
					"url": "https://iliad.ai/plan"
				},
				{
					"@type": "Offer",
					"price": "20",
					"priceCurrency": "USD",
					"description": "Iliad Pro: Full access for $20/month.",
					"url": "https://iliad.ai/plan"
				}
			],
			"featureList": [
				"Texture generation",
				"Seamless texture generation",
				"Granular control over generation",
				"Pressure-sensitive brush input",
				"Mobile support"
			],
			"publisher": {
				"@type": "Organization",
				"name": "Iliad",
				"url": "https://iliad.ai"
			},
			"image": "https://iliad.ai/brand/iliad-logo-light.svg"
		}
	</script>
	<link rel="modulepreload" href={AQUEDUCT_PROJECT_JS_PATH} />
	<!-- Aqueduct landing text resources are fetched by the early app.html promise cache. -->
</svelte:head>

<LandingPageLayout
	bind:user
	bind:loginOrSignUpModal
	bind:loginOrSignupWhichModal
	bind:redirectAfterLogin
>
	<!-- google -->
	<!-- <div class="h-24 sm:h-48"></div> -->

	<div class="h-12 sm:h-24"></div>

	<!-- Hero section: two-column on lg+, stacked on smaller -->
	<div
		class="w-full max-w-5xl px-6 z-10 flex flex-col items-center lg:flex-row lg:items-start lg:gap-0"
	>
		<!-- Left column: branding + description + CTA -->
		<div
			class="flex-1 flex flex-col items-start max-w-[340px] sm:max-w-[450px] lg:max-w-none lg:translate-y-[55px] lg:pl-2"
		>
			<IliadLogo class="h-[56px] w-[172px] sm:h-[64px] sm:w-[196.5px]" label="" />
			<div class="h-12"></div>

			<h2 class="text-[17px] sm:text-[22px] text-left dark:text-zinc-200">
				Create procedural material graphs.
			</h2>

			<div class="h-8 sm:h-12"></div>

			<div
				class="text-[14px] w-[340px] sm:w-[430px] lg:w-[470px] leading-relaxed text-zinc-900 dark:text-zinc-300 text-left"
			>
				<p>
					Try <span class="font-semibold">Aqueduct</span>, a new node-based procedural material
					engine made by Iliad, now in open early access.
				</p>
			</div>
			<div class="h-8"></div>
			<a
				href={aqueductLink}
				class="inline-flex h-10 w-full sm:w-[330px] items-center justify-center gap-2 rounded bg-zinc-900 px-6 text-sm font-semibold text-zinc-50 shadow-lg hover:bg-zinc-800 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-zinc-200"
				on:click={() => analytics.capture('cta_clicked', { text: 'explore_aqueduct' })}
			>
				Explore Aqueduct
				<i class="fa-solid fa-arrow-right text-xs"></i>
			</a>
		</div>

		<!-- Right column: interactive demo -->
		<div
			class="mt-[88px] lg:mt-0 lg:translate-x-[40px] w-[495px] flex flex-col items-center lg:items-start"
		>
			<AqueductDemo
				on:ready={() => enableTextureShowcaseImages('aqueduct-ready')}
				on:unavailable={() => enableTextureShowcaseImages('aqueduct-unavailable')}
				on:failed={() => enableTextureShowcaseImages('aqueduct-failed')}
			/>
		</div>
	</div>

	<div class="w-full flex flex-col items-center relative z-10">
		<div class="h-20"></div>

		<div class="w-[340px] sm:w-[450px] z-10 flex flex-col items-start">
			<div class="text-[16px] leading-relaxed text-zinc-900 dark:text-zinc-200 text-left">
				<p>Diffusion tools</p>
			</div>
		</div>

		<!-- <div
			class="w-full h-full absolute bg-cover dark:hidden"
			style="
					background-image: url('/pale-blue3.avif');
					background-repeat: no-repeat;
					background-position: center;
					mask-composite: intersect;
					mask-image: linear-gradient(0deg, transparent 0%, black 50%, transparent 100%),
					linear-gradient(90deg, transparent 0%, black 50%, transparent 100%);"
		></div> -->

		<!-- Texture Card -->
		<div class="h-4"></div>
		<CTAHeadless
			{user}
			href={texturesLink}
			eventText="open_textures"
			{loginOrSignUpModal}
			{loginOrSignupWhichModal}
			bind:redirectAfterLogin
			allowAnonymous={true}
		>
			<ToolCard title="Texture Canvas" description="Create custom seamlessly tileable textures.">
				<span slot="icon">
					<img
						src="/noun-textures-67667.svg"
						alt=""
						class="w-[30px] h-8 ml-[-5px] mr-[27.5px] sm:mr-[15.5px] dark:hidden"
					/>
					<img
						src="/noun-textures-67667-cyan400.svg"
						alt=""
						class="hidden dark:block w-[30px] h-8 ml-[-5px] mr-[27.5px] sm:mr-[15.5px]"
					/>
				</span>
			</ToolCard>
		</CTAHeadless>

		<div class="h-3"></div>

		<!-- Materials Card -->
		<CTAHeadless
			{user}
			href={materialsLink}
			eventText="open_materials"
			{loginOrSignUpModal}
			{loginOrSignupWhichModal}
			bind:redirectAfterLogin
			allowAnonymous={true}
		>
			<ToolCard title="Material Generator" description="Create PBR material maps from a texture.">
				<i slot="icon" class="fa-solid fa-cube text-2xl text-cyan-600 mr-4 dark:text-cyan-400"></i>
			</ToolCard>
		</CTAHeadless>

		<div class="h-3"></div>

		<!-- Reference to Texture Card -->
		<CTAHeadless
			{user}
			href="/reference-to-texture"
			eventText="open_reference_to_texture"
			{loginOrSignUpModal}
			{loginOrSignupWhichModal}
			bind:redirectAfterLogin
			allowAnonymous={true}
		>
			<ToolCard
				title="Reference to Texture"
				description="Turn your reference into a seamless texture."
			>
				<i
					slot="icon"
					class="fa-solid fa-arrow-up-from-bracket text-2xl text-cyan-600 ml-1 mr-4 dark:text-cyan-400"
				></i>
			</ToolCard>
		</CTAHeadless>
	</div>

	{#if showcaseTextures.length > 0}
		<div class="h-12 sm:h-24"></div>

		<div class="w-fit z-10">
			<h2 class="text-[16px] text-zinc-900 dark:text-zinc-300">Texture canvas gallery</h2>
			<div class="h-8"></div>
			<TextureShowcase
				textures={showcaseTextures}
				basePath="/textures"
				loadImages={loadTextureShowcaseImages}
			/>
		</div>
	{/if}

	<div class="h-12"></div>
	<div class="opacity-[0.0000000000000000001] pointer-events-none">
		<a
			href="https://theresanaiforthat.com/ai/iliad/?ref=featured&v=4042902"
			target="_blank"
			rel="nofollow"
			><img
				alt="taaft-ref"
				width="224"
				height="46"
				src="https://media.theresanaiforthat.com/featured-on-taaft.png?width=600"
				loading="lazy"
			/></a
		>
	</div>
	<div class="h-12"></div>

	<!-- Footer -->
	<footer
		class="w-80 sm:w-full flex flex-wrap justify-center gap-3 sm:gap-5 px-3 py-6 text-xs text-primary-500 dark:text-zinc-600"
	>
		<!-- <a href="/about" class="hover:text-primary-600 dark:hover:text-cyan-300">About</a> -->
		<a href="/seamless-texture-generator" class="hover:text-primary-600 dark:hover:text-cyan-300"
			>Seamless texture generator</a
		>
		<a href="/utilities" class="hover:text-primary-600 dark:hover:text-cyan-300">Utilities</a>
		<a href="/privacy-policy" class="hover:text-primary-600 dark:hover:text-cyan-300">Privacy</a>
		<a href="/terms-of-service" class="hover:text-primary-600 dark:hover:text-cyan-300">Terms</a>
	</footer>
</LandingPageLayout>
